/**
 * Deterministic publish rewards.
 *
 * Formulas (integer, bounded — documented for tests):
 *   reachGain     = score.reach                          (never spent later)
 *   followersGain = clamp(floor(reach / 80), 3, 25)
 *   cashGain      = clamp(8 + floor(reach / 200), 8, 40)
 *   credDelta     = score.credDelta                      (may be negative)
 *
 * No Reach multipliers from store purchases. No energy. No real money.
 */
import type { ScoreBreakdown } from "@desk/core";
import type { AudienceResult, PublishRewards } from "./types.js";

/**
 * Best-score reward tuning (Job B) — deterministic, integer, in ONE place.
 *
 * A landing pays followers (and Bits) scaled by MATCH QUALITY: the crowd `fit`
 * (0..1) plus a bonus for a `clean` landing (no receipts). A perfect clean
 * landing pays `BASE + FIT_SPAN + CLEAN_BONUS`; a marginal landing (just over
 * the line, low fit, not clean) pays roughly `BASE`. Not landed → 0.
 *
 *   followers: perfect clean ≈ 6 + 6 + 2 = 14 · marginal ≈ 6–8
 *   bits:      perfect clean ≈ 4 + 4 + 2 = 10 · marginal ≈ 4–6
 *
 * A one-time 3/3 CLEAN-SWEEP bonus is paid the first time a level lands all its
 * crowds. Nothing here ever feeds `@desk/core` scoring.
 */
export const REWARD_TUNING = {
  followers: { base: 6, fitSpan: 6, cleanBonus: 2 },
  cash: { base: 4, fitSpan: 4, cleanBonus: 2 },
  /** One-time bonus when a level first reaches 3/3 (all crowds landed). */
  sweep: { followers: 10, cash: 8 },
} as const;

function fit01(result: Pick<AudienceResult, "fitMilli">): number {
  const f = (result.fitMilli ?? 0) / 1000;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/**
 * Followers a per-audience result is WORTH (not the delta — the absolute best
 * this result would bank). 0 unless it landed; otherwise scales with `fit` and
 * adds a bonus when `clean`. Deterministic integer.
 */
export function followersForResult(result: Pick<AudienceResult, "landed" | "clean" | "fitMilli">): number {
  if (!result.landed) return 0;
  const t = REWARD_TUNING.followers;
  return Math.round(t.base + t.fitSpan * fit01(result) + (result.clean ? t.cleanBonus : 0));
}

/** Bits/cash a per-audience result is WORTH (same shape as followers). */
export function cashForResult(result: Pick<AudienceResult, "landed" | "clean" | "fitMilli">): number {
  if (!result.landed) return 0;
  const t = REWARD_TUNING.cash;
  return Math.round(t.base + t.fitSpan * fit01(result) + (result.clean ? t.cleanBonus : 0));
}

export function computePublishRewards(score: ScoreBreakdown): PublishRewards {
  const reach = Math.max(0, score.reach | 0);
  const followersGain = clampInt(Math.floor(reach / 80), 3, 25);
  const cashGain = clampInt(8 + Math.floor(reach / 200), 8, 40);
  return {
    reachGain: reach,
    followersGain,
    cashGain,
    credDelta: score.credDelta | 0,
  };
}

export function applyRewardsToEconomy(
  economy: { reach: number; followers: number; cash: number; cred: number },
  rewards: PublishRewards,
): { reach: number; followers: number; cash: number; cred: number } {
  return {
    reach: economy.reach + rewards.reachGain,
    followers: economy.followers + rewards.followersGain,
    cash: economy.cash + rewards.cashGain,
    cred: economy.cred + rewards.credDelta,
  };
}

function clampInt(n: number, min: number, max: number): number {
  if (n < min) return min;
  if (n > max) return max;
  return n | 0;
}
